import { Dialog, Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CompanyService } from "../../../services/api";
import FormInput from "../../molecules/FormInput";
import FormTemplate from "../../templates/FormTemplate";
import useRefresh from "../../../hooks/useRefresh";
import { Address, Company } from "../../../services/swagger";
import useBreakpoint from "../../../hooks/useBreakpoint";
import useConfirm from "../../../hooks/useConfirm";
import DeleteIcon from "@mui/icons-material/Delete";
import { useMutation } from "@tanstack/react-query";
import AddressGrid from "../../molecules/AddressGrid";
import CompanyContacts from "../../molecules/CompanyContacts";

interface EditCompanyDialogProps {
  company?: Company;
  onSubmit?: () => void;
  onClose?: () => void;
}

export default function EditCompanyDialog(props: EditCompanyDialogProps) {
  const { company } = props;
  const { t } = useTranslation("Company");
  const refresh = useRefresh();
  const [name, setName] = useState("");
  const [regNo, setRegNo] = useState("");
  const [emails, setEmails] = useState<string[]>([]);
  const [phones, setPhones] = useState<string[]>([]);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [extRef, setExtRef] = useState("");
  const confirm = useConfirm();

  const breakpoint = useBreakpoint();

  useEffect(() => {
    if (!company) return;
    setName(company.name);
    setRegNo(company.regNo);
    setEmails(company.emails);
    setPhones(company.phones);
    setAddresses(
      company.addresses.map((a) => ({
        ...a,
        line1: a.line1 ?? "",
        city: a.city ?? "",
        state: a.state ?? "",
        postalCode: a.postalCode ?? "",
      })),
    );
    setExtRef(company.extRef);
  }, [props.company]);

  const { mutate: handleSubmit } = useMutation({
    mutationFn: async () => {
      if (!company?._id) return;
      const res = await CompanyService.updateCompany({
        id: company._id,
        updateCompanyBody: {
          addresses,
          emails,
          extRef,
          phones,
          name,
          regNo,
        },
      });
      return res.data.data;
    },
    onSuccess: (data) => {
      if (data) {
        refresh();
        props.onClose?.();
      }
    },
  });

  const { mutate: handleDelete } = useMutation({
    mutationFn: async () => {
      if (!company?._id) return;
      const res = await CompanyService.deleteCompany({
        id: company._id,
      });
      return res?.data?.data;
    },
    onSuccess: (data) => {
      if (data) {
        refresh();
        props.onClose?.();
      }
    },
  });

  return (
    <Dialog
      fullScreen={!breakpoint.sm}
      open={Boolean(props.company)}
      onClose={props.onClose}
    >
      <FormTemplate
        title={t("EditCompany")}
        onSubmit={handleSubmit}
        onCancel={props.onClose}
        options={[
          {
            icon: <DeleteIcon color="error" />,
            label: t("DeleteCompany"),
            onClick: () => confirm(handleDelete),
          },
        ]}
        confirm
      >
        <Stack spacing={2} sx={{ minWidth: { xs: 1, sm: 400, md: 500 } }}>
          <FormInput
            field="name"
            label={t("Name")}
            onChange={(v) => {
              setName(v as string);
            }}
            type="string"
            required
            value={name}
          />
          <FormInput
            field="regNo"
            label={t("RegNo")}
            onChange={(v) => {
              setRegNo(v as string);
            }}
            type="string"
            value={regNo}
          />
          <FormInput
            field="phones"
            label={t("Phones")}
            onChange={(v) => {
              setPhones(v as string[]);
            }}
            type="chip-list"
            value={phones}
          />
          <FormInput
            field="emails"
            label={t("Emails")}
            onChange={(v) => {
              setEmails(v as string[]);
            }}
            type="chip-list"
            value={emails}
          />
          <FormInput
            field="extRef"
            label={t("ExtRef")}
            onChange={(v) => {
              setExtRef(v as string);
            }}
            type="string"
            value={extRef}
          />
          <AddressGrid
            addresses={addresses}
            onChange={(addressList) => setAddresses(addressList)}
          />

          {company?._id && <CompanyContacts companyId={company._id} />}
        </Stack>
      </FormTemplate>
    </Dialog>
  );
}
